import {
  SatConfigurationStatus,
  SatLocale,
  SatServiceProvider,
  SatStatusToStepMap,
  ServiceToSatServiceProviderMap,
} from "@/constants/security-awareness.ts";
import { AccessPermissionType, Service } from "@/constants/cloud-apps.ts";
import type { UserLabel } from "@/_store/users-settings/user-labels.module.ts";

export interface SatProtectedUser {
  email: string;
  firstName: string;
  lastName: string;
  groups: { id: string; name: string }[];
  labels: UserLabel[];
}

export interface SatEmailProviderConfig {
  customDomain: string;
  status: SatConfigurationStatus;
  testEmailAddresses: string[];
  protectedGroups: { id: string; name?: string }[];
  protectedUserEmails: string[];
  protectedLabels: UserLabel[];
  protectedUsers?: SatProtectedUser[];
}

export interface SatSettings {
  emailProviderConfigurations: SatEmailProvidersConfiguration;
  preferredLanguageCode: SatLocale;
}

export type SatEmailProvidersConfiguration = Record<
  Partial<SatServiceProvider>,
  SatEmailProviderConfig
>;

export interface SatConnection {
  name: string | Service; // custom domain name or service provider
  userType: AccessPermissionType;
  domain: string;
  testEmailAddresses: string[];
  usersList: SatProtectedUser[]; // users added individually, users from groups and labels
  users: { email: string }[];
  groups: {
    groupName: string;
    groupId: string;
  }[];
  labels: UserLabel[];
  totalUsers: number;
  step: number;
  isIncomplete: boolean;
}

export function normalizeSatSettingsResponse(
  emailProviderConfigurations: SatEmailProvidersConfiguration
): SatConnection[] {
  return Object.entries(emailProviderConfigurations).map(
    ([rawKey, config]: [string, SatEmailProviderConfig]) => {
      const key = rawKey as SatServiceProvider;
      const step = SatStatusToStepMap[config.status];
      const hasSpecificUsersSelected =
        config.protectedGroups?.length ||
        config.protectedUserEmails?.length ||
        config.protectedLabels?.length;
      const userType = hasSpecificUsersSelected
        ? AccessPermissionType.SPECIFIC_USERS
        : AccessPermissionType.ALL_USERS;

      return {
        name:
          key === SatServiceProvider.CUSTOM
            ? config.customDomain!
            : ServiceToSatServiceProviderMap[key as SatServiceProvider]!,
        userType,
        domain: config.customDomain,
        testEmailAddresses: config.testEmailAddresses,
        usersList: (config.protectedUsers as SatProtectedUser[]) || [],
        users: config.protectedUserEmails
          ? config.protectedUserEmails.map((email) => ({ email }))
          : [],
        groups: config.protectedGroups
          ? config.protectedGroups.map((group) => ({
              groupName: group.name!,
              groupId: group.id,
            }))
          : [],
        labels: config.protectedLabels || [],
        totalUsers: config.protectedUsers?.length || 0,
        step,
        isIncomplete: step < 6,
      };
    }
  );
}
